import React, {
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { DataRoomNodeReport } from '../../../Reports/types';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import ReportsEmptyState from '../../../Reports/components/ReportsEmptyState';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  BODY_COL_CONTENT,
  COPY_TRANSLATE_MAP,
  FILE_ACTIVITY_COLUMNS,
  isSortName,
  SortName,
  SORT_NAME_MAP,
  POPUP_SORT_NAME,
} from './data';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DocumentAnalytics } from 'router/subrouters/DataRoom/components/DocumentAnalytics';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import ManageFileAccess from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import ViewAndDownloadFileStatsModal from './components/ViewAndDownloadFileStatsModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { getHasElementsInArray } from 'utils';
import useFileActivityQuery from './hooks/useFileActivityQuery';
import TimelineTag from '../TimelineTag';

import './FileActivity.styles.scss';
import './FileActivity.styles.responsive.scss';

const checkIsAnalyticsEmpty = ({
  numberOfViews,
  numberOfSecondsSpent,
}: DataRoomNodeReport) => !numberOfViews && !numberOfSecondsSpent;

const checkDoesntHaveAccess = (
  node: DataRoomNode,
  doesntHaveAccess?: boolean,
) => doesntHaveAccess || !node._id;

type FileActivityProps = {
  className?: string;
  withManageAccessCell?: boolean;
  updateNodes?: () => void;
};

const FileActivity: React.FC<FileActivityProps> = (props) => {
  const { className, withManageAccessCell, updateNodes } = props;

  const [sort, setSort] = useState(`-${SORT_NAME_MAP.views}`);
  const [selectedAnalytics, setSelectedAnalytics] = useState('');
  const [selectedViewAndDownloadStats, setSelectedViewAndDownloadStats] =
    useState<DataRoomNode>();

  const {
    dataRoom: { _id: dataRoomId, name: dataRoomName, userId, dealRoomId },
    qaSide,
    doesntHaveAccess,
  } = useContext(DataRoomContext);
  const { openNode } = useContext(DocumentViewContext);

  const [viewAndDownloadFileStatsModal, viewAndDownloadFileStatsModalRef] =
    useCallbackRef<ModalRef>();

  const withManageAccess = !!withManageAccessCell && !doesntHaveAccess;

  const { t } = useTranslation();

  const { reports, isLoading } = useFileActivityQuery({
    dataRoomId,
    sort,
  });

  const classes = classNames(
    'file-activity',
    { loading: isLoading },
    className,
  );

  const renderSortButton = useCallback(
    (sortName: SortName, content?: ReactNode) => (
      <SortingButton
        ascDefault={false}
        content={content}
        sortingValue={sortName}
        popupContent={t(POPUP_SORT_NAME[sortName])}
        sort={sort}
        updateSort={setSort}
      />
    ),
    [sort, t],
  );

  const drawerRefs = useRef<SideDrawerRef[]>([]);

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      <div className="header">
        <p>{t('topFiles')}</p>
        <TimelineTag />
      </div>

      {!getHasElementsInArray(reports) ? (
        isLoading ? (
          <Loader overlay={false} />
        ) : (
          <ReportsEmptyState />
        )
      ) : (
        <div className="file-activity__scrolling-container">
          <div className="table">
            <div className="thead">
              <div className="tr">
                {FILE_ACTIVITY_COLUMNS.map((col) => (
                  <div className={classNames('th', col)} key={col}>
                    {isSortName(SORT_NAME_MAP[col])
                      ? renderSortButton(
                          SORT_NAME_MAP[col],
                          t(COPY_TRANSLATE_MAP[col]),
                        )
                      : t(COPY_TRANSLATE_MAP[col])}
                  </div>
                ))}
              </div>
            </div>
            <div className="tbody">
              {reports.map((report, i) => {
                const disabled = checkDoesntHaveAccess(
                  report.dataRoomNode,
                  doesntHaveAccess,
                );

                const analyticsDisabled = checkIsAnalyticsEmpty(report);

                return (
                  <React.Fragment key={report._id}>
                    <div className="tr">
                      {FILE_ACTIVITY_COLUMNS.map((col) => {
                        const sortName = SORT_NAME_MAP[col];
                        const isSelected = sortName && sort?.includes(sortName);

                        return BODY_COL_CONTENT[col](report, {
                          isSelected,
                          hasAnalytics: !disabled,
                          withManageAccessCell: withManageAccess,
                          analyticsDisabled,
                          drawerRefOpen: () =>
                            drawerRefs.current[i]?.setOpen(true),
                          openFile: () => {
                            if (disabled) return;
                            openNode(report.dataRoomNode, {
                              dataRoomName,
                              ownerId: userId,
                              userSide: qaSide,
                              dealRoomId,
                            });
                          },
                          openAnalytics: () => {
                            if (disabled || analyticsDisabled) return;
                            if (selectedAnalytics === report.dataRoomNode._id)
                              setSelectedAnalytics('');
                            else setSelectedAnalytics(report.dataRoomNode._id);
                          },
                          openViewAndDownloadFileStatsModal: () => {
                            setSelectedViewAndDownloadStats(
                              report.dataRoomNode,
                            );
                            viewAndDownloadFileStatsModal?.open();
                          },
                          t,
                        });
                      })}

                      <ManageFileAccess
                        ref={(ref) => (drawerRefs.current[i] = ref)}
                        nodes={[report.dataRoomNode]}
                        parentId={report.dataRoomNode?.parentId}
                        updateNodes={updateNodes}
                      />
                    </div>
                    <div className="tr">
                      <div className="td">
                        <DocumentAnalytics
                          node={report.dataRoomNode}
                          isOpen={report.dataRoomNode._id === selectedAnalytics}
                          getDocumentAnalytics={
                            dataRoomApi.nodes.getFileAnalytics
                          }
                        />
                      </div>
                    </div>

                    <ViewAndDownloadFileStatsModal
                      ref={viewAndDownloadFileStatsModalRef}
                      dataRoomNode={selectedViewAndDownloadStats}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileActivity;
